import {
    Component,
    Element,
    Event,
    h,
    Host,
    Prop,
    Watch,
} from "@stencil/core";
import type { EventEmitter } from "@stencil/core"

import type { PageConfig } from "../../../config/page-configs/page-configs";
import { isHTMLElement } from "../../../utils/is-html-element";
import {
    preventSameAlert,
    shouldAlertOpen,
} from "../../../utils/cookies/localStorage";
import {
    BOT_ALERT,
    BOT_AVATAR,
    ALERT_YES_BTN,
    ALERT_NO_BTN,
} from "../../../global/variables";

@Component({
    tag: "c-alert",
    styleUrl: "c-alert.scss",
    shadow: true,
})
export class CAlert {
    delay = 2000;
    yesButton: HTMLButtonElement;
    activeElementBeforeOpening: HTMLElement;
    @Event() userConfirm: EventEmitter;
    @Event() alertOpenChange: EventEmitter<boolean>;
    @Prop({ mutable: true }) isOpen = false;
    @Prop() pageConfig: PageConfig;
    @Prop() isDialogOpen: boolean;
    @Watch("isDialogOpen")
    handleIsDialogOpen(isDialogOpen: boolean) {
        if (isDialogOpen) {
            this.preventFutureAlerts();
            if (this.isOpen) {
                // If consumer opened bot via custom button, we need to close the alert
                // If the user clicked "yes" to open the bot, the alert is already closed
                // This if statement prevents close() to be called a second time
                this.close();
            }
        }
    }
    @Element() hostElement: HTMLElement;
    // trapFocus: (e: KeyboardEvent) => void

    componentDidLoad() {
        setTimeout(() => this.open(), this.delay);
        // this.trapFocus = trapFocus(this.hostElement)
    }

    open() {
        if (!shouldAlertOpen(this.pageConfig)) {
            return;
        }
        this.isOpen = true;
        this.alertOpenChange.emit(true);
        // document.addEventListener('keydown', this.trapFocus)
        this.preventFutureAlerts();
        if (isHTMLElement(document.activeElement)) {
            this.activeElementBeforeOpening = document.activeElement;
        }
        this.yesButton.focus();
    }

    close() {
        this.isOpen = false;
        this.alertOpenChange.emit(false);
        // document.removeEventListener('keydown', this.trapFocus)
    }

    handleYes() {
        this.close();
        this.userConfirm.emit(true);
    }

    handleNo() {
        this.hostElement.classList.add("close-animation");
        if (isHTMLElement(this.activeElementBeforeOpening)) {
            this.activeElementBeforeOpening.focus();
        }
        const textFadeOutDekay = 500;
        const animationDuration = 240;
        setTimeout(() => this.close(), textFadeOutDekay + animationDuration);
    }

    preventFutureAlerts() {
        preventSameAlert(this.pageConfig);
    }

    render() {
        // Future Task Firefox this.yesbutton undefined + we need timeout.
        // Better to always have alert in dom and just add class "animate-in"
        // to get rid of setTimeout()
        return (
            <Host
                role="alertdialog"
                title={BOT_ALERT}
                aria-label={BOT_ALERT}
                aria-describedby="cb-alert-text"
                class={this.isOpen ? "open-animation" : ""}
                aria-hidden={this.isOpen ? null : "true"}
            >
                <div id="cb-alert" class="noPrint">
                    <span
                        id="cb-alert-text"
                        innerHTML={this.pageConfig.alert.text}
                    ></span>
                    <div id="cb-alert-buttons">
                        <button
                            id="cb-alert-yes-button"
                            tabindex={this.isOpen ? 0 : -1}
                            onClick={this.handleYes.bind(this)}
                            ref={(el) => (this.yesButton = el!)}
                            title={ALERT_YES_BTN}
                            aria-label={ALERT_YES_BTN}
                            data-analytics-track="true"
                            data-analytics-action="ButtonClicks TCB: Klick"
                            data-analytics-value="Yes"
                            data-analytics-name="TCB: Alert Popup"
                            data-analytics-category="TCB | ButtonClicks"
                        >
                            {this.pageConfig.alert.id === 'QuestionsAboutFormularUA' ? 'Так' : 'Ja' }
                        </button>
                        <button
                            id="cb-alert-no-button"
                            tabindex={this.isOpen ? 0 : -1}
                            onClick={this.handleNo.bind(this)}
                            title={ALERT_NO_BTN}
                            aria-label={ALERT_NO_BTN}
                            data-analytics-track="true"
                            data-analytics-action="ButtonClicks TCB: Klick"
                            data-analytics-value="No"
                            data-analytics-name="TCB: Alert Popup"
                            data-analytics-category="TCB | ButtonClicks"
                        >
                            {this.pageConfig.alert.id === 'QuestionsAboutFormularUA' ? 'Ні' : 'Nein' }
                        </button>
                    </div>
                </div>
                <div
                    id="cb-alert-bot-avatar"
                    class="avatar-container noPrint"
                    aria-label={BOT_AVATAR}
                >
                    <c-avatar-button></c-avatar-button>
                </div>
            </Host>
        );
    }
}
